import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  getUserInfo,
  getFavorites,
  getRatings,
  getFollowers,
  getFollowing,
  followUser,
  unfollowUser
} from '../services/api';
import BookCard from '../components/BookCard';
import './OtherUserProfilePage.css';
import { useLocation } from 'react-router-dom';

function UserProfilePage() {
  const location = useLocation();
  const { userId } = useParams();
  const navigate = useNavigate();
  const loggedInUserId = Number(localStorage.getItem('userId'));
  const isOwnProfile = Number(userId) === loggedInUserId;
  const [modalLoading, setModalLoading] = useState(false);

  // User data
  const [user, setUser] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Stats
  const [followersCount, setFollowersCount] = useState(0);
  const [followingCount, setFollowingCount] = useState(0);
  const [favoritesCount, setFavoritesCount] = useState(0);
  const [ratingsCount, setRatingsCount] = useState(0);
  
  // Books preview
  const [favoriteBooks, setFavoriteBooks] = useState([]);
  const [ratedBooks, setRatedBooks] = useState([]);
  
  // Follow state
  const [isFollowing, setIsFollowing] = useState(false);
  
  // Modal
  const [showModal, setShowModal] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const [modalUsers, setModalUsers] = useState([]);

  useEffect(() => {
    if (userId) {
      fetchUserData();
    } else if (loggedInUserId) {
      navigate(`/profile/${loggedInUserId}`, { replace: true });
    } else {
      navigate('/signin', { replace: true });
    }
  }, [userId, loggedInUserId, navigate]);

  // ↓ You can remove this second one
  useEffect(() => {
    if (userId) {
      fetchUserData();
    }
  }, [userId]);

  const fetchUserData = async () => {
    setLoading(true);
    setError(null);
    setUser({});
    setFavoriteBooks([]);
    setRatedBooks([]);
    setFollowersCount(0);
    setFollowingCount(0);
    setFavoritesCount(0);
    setRatingsCount(0);
    setIsFollowing(false);
    
    try {
      // Get user info
      const userData = await getUserInfo(Number(userId));
      setUser(userData);
      
      // Get followers and following
      const [followers, following] = await Promise.all([
        getFollowers(Number(userId)),
        getFollowing(Number(userId))
      ]);
      setFollowersCount(followers.length);
      setFollowingCount(following.length);
      
      // Get books data
      const [favorites, ratings] = await Promise.all([
        getFavorites(Number(userId)),
        getRatings(Number(userId))
      ]);
      setFavoriteBooks(favorites || []);
      setRatedBooks(ratings || []);
      setFavoritesCount(favorites.length);
      setRatingsCount(ratings.length);
      
      // Check if logged-in user follows this profile
      if (!isOwnProfile && loggedInUserId) {
        const myFollowing = await getFollowing(loggedInUserId);
        setIsFollowing(myFollowing.some(u => u.id === Number(userId)));
      }
    } catch (err) {
      console.error('Failed to fetch user data:', err);
      setError('Failed to load user profile.');
    } finally {
      setLoading(false);
    }
  };

  const handleFollowToggle = async () => {
    if (!loggedInUserId) {
      navigate('/signin');
      return;
    }
    
    try {
      if (isFollowing) {
        await unfollowUser(loggedInUserId, Number(userId));
        setIsFollowing(false);
        setFollowersCount(prev => prev - 1);
      } else {
        await followUser(loggedInUserId, Number(userId));
        setIsFollowing(true);
        setFollowersCount(prev => prev + 1);
      }
    } catch (err) {
      console.error('Follow/unfollow error:', err);
    }
  };

  const openUserListModal = async (type) => {
    setModalTitle(type === 'followers' ? 'Followers' : 'Following');
    setShowModal(true);
    setModalLoading(true); // Start loading
  
    try {
      const idList = type === 'followers'
        ? await getFollowers(Number(userId))
        : await getFollowing(Number(userId));
  
      const enrichedUsers = await Promise.all(
        idList.map(async ({ id }) => {
          try {
            const user = await getUserInfo(id);
            return { ...user, id };
          } catch {
            return { id, username: 'Unknown', profile_pic: '' };
          }
        })
      );
  
      setModalUsers(enrichedUsers);
    } catch (err) {
      console.error(`Failed to fetch ${type}:`, err);
      setModalUsers([]);
    } finally {
      setModalLoading(false); // Stop loading
    }
  };
  

  const closeModal = () => {
    setShowModal(false);
    setModalUsers([]);
  };

  const navigateToUserProfile = (targetUserId) => {
    closeModal();
    if (Number(targetUserId) === Number(userId)) return; // same profile, do nothing
    // Navigate and force fetch with key
    navigate(`/profile/${targetUserId}`, { replace: false });
  };

  // Skeleton UI for loading state
  if (loading) {
    return (
      <div className="profile-page">
        <div className="profile-header skeleton">
          <div className="profile-avatar skeleton-element">
            <div className="shimmer-wrapper">
              <div className="shimmer"></div>
            </div>
          </div>
          <div className="profile-info">
            <div className="skeleton-username skeleton-element">
              <div className="shimmer-wrapper">
                <div className="shimmer"></div>
              </div>
            </div>
            
            <div className="profile-stats">
              {[1, 2, 3, 4].map((_, idx) => (
                <div key={`stat-${idx}`} className="stat">
                  <span className="stat-count skeleton-element">
                    <div className="shimmer-wrapper">
                      <div className="shimmer"></div>
                    </div>
                  </span>
                  <span className="stat-label skeleton-element">
                    <div className="shimmer-wrapper">
                      <div className="shimmer"></div>
                    </div>
                  </span>
                </div>
              ))}
            </div>
            
            <div className="skeleton-button skeleton-element">
              <div className="shimmer-wrapper">
                <div className="shimmer"></div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="profile-bio skeleton">
          <div className="skeleton-bio-title skeleton-element">
            <div className="shimmer-wrapper">
              <div className="shimmer"></div>
            </div>
          </div>
          <div className="skeleton-bio-content skeleton-element">
            <div className="shimmer-wrapper">
              <div className="shimmer"></div>
            </div>
          </div>
        </div>
        
        {/* Skeleton for Books Sections */}
        {[1, 2].map((section, idx) => (
          <div key={`section-${idx}`} className="books-section skeleton">
            <div className="section-header">
              <div className="skeleton-section-title skeleton-element">
                <div className="shimmer-wrapper">
                  <div className="shimmer"></div>
                </div>
              </div>
            </div>
            
            <div className="books-preview">
              {[1, 2, 3, 4].map((_, bookIdx) => (
                <div key={`book-${section}-${bookIdx}`} className="skeleton-book-card skeleton-element">
                  <div className="skeleton-book-cover">
                    <div className="shimmer-wrapper">
                      <div className="shimmer"></div>
                    </div>
                  </div>
                  <div className="skeleton-book-info">
                    <div className="skeleton-book-title skeleton-element">
                      <div className="shimmer-wrapper">
                        <div className="shimmer"></div>
                      </div>
                    </div>
                    <div className="skeleton-book-author skeleton-element">
                      <div className="shimmer-wrapper">
                        <div className="shimmer"></div>
                      </div>
                    </div>
                    <div className="skeleton-book-rating skeleton-element">
                      <div className="shimmer-wrapper">
                        <div className="shimmer"></div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="profile-page">
        <div className="error-container">
          <h2>Error</h2>
          <p>{error}</p>
          <button onClick={() => navigate('/')} className="error-button">
           Go to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-page">
      {/* Profile Header Section */}
      <div className="profile-header">
        <div className="profile-avatar">
          <img
            src={user.profile_pic || '/resources/blank-profile.webp'}
            alt={`${user.username}'s avatar`}
          />
        </div>
        <div className="profile-info">
          <h1 className="profile-username">@{user.username || 'User'}</h1>
          <div className="profile-stats">
            <div className="stat" onClick={() => openUserListModal('followers')}>
              <span className="stat-count">{followersCount}</span>
              <span className="stat-label">Followers</span>
            </div>
            <div className="stat" onClick={() => openUserListModal('following')}>
              <span className="stat-count">{followingCount}</span>
              <span className="stat-label">Following</span>
            </div>
            <div className="stat">
              <span className="stat-count">{favoritesCount}</span>
              <span className="stat-label">Favorites</span>
            </div>
            <div className="stat">
              <span className="stat-count">{ratingsCount}</span>
              <span className="stat-label">Ratings</span>
            </div>
          </div>
          {!isOwnProfile && loggedInUserId && (
            <button
              className={`follow-button ${isFollowing ? 'following' : ''}`}
              onClick={handleFollowToggle}
            >
              {isFollowing ? 'Unfollow' : 'Follow'}
            </button>
          )}
        </div>
      </div>
      
      {/* Bio Section */}
      {user.bio && (
        <div className="profile-bio">
          <h3>About</h3>
          <p>{user.bio}</p>
        </div>
      )}
      
      {/* Books Sections */}
      <div className="books-section">
        <div className="section-header">
          <h2>Favorite Books</h2>
        </div>
        {favoriteBooks.length > 0 ? (
          <div className="books-preview">
            {favoriteBooks.slice(0, 4).map((book) => (
              <BookCard
                key={book.id}
                book={book}
                showDetailsLink={true}
                linkTo={`/favorite/${book.id}`}
                showRatingButton={false}
                showFavoriteButton={false}
                isFavorite={true}
              />
            ))}
          </div>
        ) : (
          <div className="empty-books">
            <span className="empty-icon">📚</span>
            <p>No favorite books yet</p>
          </div>
        )}
      </div>
      
      <div className="books-section">
        <div className="section-header">
          <h2>Recently Rated</h2>
        </div>
        {ratedBooks.length > 0 ? (
          <div className="books-preview">
            {ratedBooks.slice(0, 4).map((book) => (
              <BookCard
                key={book.id}
                book={book}
                showRating={true}
                userRating={book.userRating}
                showDetailsLink={true}
                linkTo={`/favorite/${book.id}`}
                showRatingButton={false}
                showFavoriteButton={false}
                isFavorite={favoriteBooks.some(fav => fav.id === book.id)}
              />
            ))}
          </div>
        ) : (
          <div className="empty-books">
            <span className="empty-icon">⭐</span>
            <p>No rated books yet</p>
          </div>
        )}
      </div>
      
      {/* Modal for followers/following */}
      {showModal && (
        <div className="modal-backdrop" onClick={closeModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>{modalTitle}</h3>
            <button className="close-modal" onClick={closeModal}>✖</button>
            {modalLoading ? (
              <div className="modal-shimmer-loader" />
            ) : modalUsers.length > 0 ? (
              <ul className="modal-user-list">
                {modalUsers.map((u, index) => (
                  <li key={u.id || `user-${index}`} className="modal-user-item">
                    <img
                      src={u.profile_pic || '/resources/blank-profile.webp'}
                      alt={u.username || 'Unknown'}
                      className="user-avatar"
                    />
                    <span className="username">@{u.username || 'unknown'}</span>
                    <button
                      className="view-profile-btn"
                      onClick={() => navigateToUserProfile(u.id)}
                    >
                     View Profile
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="empty-message">No {modalTitle.toLowerCase()}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default UserProfilePage;